/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import * as DashboardAPI from '../services/DashboardAPIs';
import * as types from '../constants/ActionTypes';

import { filterUserResourcesToArray } from 'Utils';

const loadDashboardDataRequest = () => ({
  type: types.LOAD_DASHBOARD_DATA_REQUEST,
});
const loadDashboardDataSuccess = data => ({
  type: types.LOAD_DASHBOARD_DATA_SUCCESS,
  data,
});
const loadDashboardDataFailure = error => ({
  type: types.LOAD_DASHBOARD_DATA_FAILURE,
  error,
});
const loadDashboardData = () => dispatch => {
  dispatch(loadDashboardDataRequest());
  return DashboardAPI.getTenantDashboardData()
    .then(json => dispatch(loadDashboardDataSuccess(json)))
    .catch(error => {
      dispatch(loadDashboardDataFailure(error));
      throw error;
    });
};

const getOtherServicesRequest = () => ({
  type: types.GET_OTHER_SERVICES,
});
const getOtherServicesSuccess = data => ({
  type: types.GET_OTHER_SERVICES_SUCCESS,
  payload: data,
});
const getOtherServicesFailure = error => ({
  type: types.GET_OTHER_SERVICES_FAILURE,
  payload: error,
});

export const getOtherServices =
  () =>
  (dispatch, getState, { dataCache }) => {
    dispatch(getOtherServicesRequest());
    return DashboardAPI.getServices()
      .then(responseJson =>
        filterUserResourcesToArray(
          // add uuid property required for filtering
          responseJson.map(r => ({ ...r, uuid: r?.serviceType || '' })),
          dataCache,
          'SERVICE'
        ).map(resource => {
          // remove uuid property to maintain object structure
          const r = resource;
          delete r.uuid;
          return r;
        })
      )
      .then(json => dispatch(getOtherServicesSuccess(json)))
      .catch(error => {
        dispatch(getOtherServicesFailure(error));
      });
  };

const deleteOtherServiceRequest = () => ({
  type: types.DELETE_OTHER_SERVICE,
});
const deleteOtherServiceSuccess = id => ({
  type: types.DELETE_OTHER_SERVICE_SUCCESS,
  payload: id,
});
const deleteOtherServiceFailure = error => ({
  type: types.DELETE_OTHER_SERVICE_FAILURE,
  payload: error,
});

export const deleteOtherService =
  (name, id) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(deleteOtherServiceRequest());
    const userEmailId = dataCache.get('currentUser')?.id;

    return DashboardAPI.deleteService(name, userEmailId)
      .then(() => dispatch(deleteOtherServiceSuccess(id)))
      .catch(error => {
        dispatch(deleteOtherServiceFailure(error));
        throw error;
      });
  };

const updateOtherServiceRequest = () => ({
  type: types.UPDATE_OTHER_SERVICE,
});
const updateOtherServiceSuccess = data => ({
  type: types.UPDATE_OTHER_SERVICE_SUCCESS,
  payload: data,
});
const updateOtherServiceFailure = error => ({
  type: types.UPDATE_OTHER_SERVICE_FAILURE,
  payload: error,
});

export const updateOtherService =
  service =>
  (dispatch, getState, { dataCache }) => {
    dispatch(updateOtherServiceRequest());
    const userEmailId = dataCache.get('currentUser')?.id;

    return DashboardAPI.updateService(service, userEmailId)
      .then(() => dispatch(updateOtherServiceSuccess(service)))
      .catch(error => {
        dispatch(updateOtherServiceFailure(error));
        // stops the page from leaving
        throw error;
      });
  };

const addOtherServiceRequest = () => ({
  type: types.ADD_OTHER_SERVICE,
});
const addOtherServiceSuccess = data => ({
  type: types.ADD_OTHER_SERVICE_SUCCESS,
  payload: data,
});
const addOtherServiceFailure = error => ({
  type: types.ADD_OTHER_SERVICE_FAILURE,
  payload: error,
});

export const addOtherService =
  service =>
  (dispatch, getState, { dataCache }) => {
    dispatch(addOtherServiceRequest());
    const userEmailId = dataCache.get('currentUser')?.id;

    return DashboardAPI.addService(service, userEmailId)
      .then(json => dispatch(addOtherServiceSuccess(json[0])))
      .catch(error => {
        dispatch(addOtherServiceFailure(error));
        // stops the page from leaving
        throw error;
      });
  };

const clearDashboardError = () => ({
  type: types.CLEAR_DASHBOARD_ERROR,
});
const clearError = () => dispatch => {
  dispatch(clearDashboardError());
};

const loadSampleAppRequest = () => ({
  type: types.LOAD_SAMPLE_APP_DETAILS_REQUEST,
});
const loadSampleAppSuccess = data => ({
  type: types.LOAD_SAMPLE_APP_DETAILS_SUCCESS,
  data,
});
const loadSampleAppFailure = error => ({
  type: types.LOAD_SAMPLE_APP_DETAILS_FAILURE,
  error,
});
// sample app content
const loadSampleAppData = () => dispatch => {
  dispatch(loadSampleAppRequest());
  return DashboardAPI.getSampleAppData().then(
    json => dispatch(loadSampleAppSuccess(json)),
    error => dispatch(loadSampleAppFailure(error))
  );
};
export const actions = {
  loadDashboardData,
  loadSampleAppData,
  getOtherServices,
  updateOtherService,
  deleteOtherService,
  addOtherService,
  clearError,
};
